import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import CountUpNumber from '../../../../../../shared/components/v3/CountUpNumber/CountUpNumber';
import BdsDivider from '../../../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import { CREATE_CHALLENGE_ROUTE } from '../../../../../../shared/constants/auth';
import { CLIENTS_PORTAL_URL } from '../../../../../../shared/constants/constants';
import { COUNT_UP_DURATION } from '../../../../../../shared/constants/reports';
import { CLICK_ACTION, HEALTH_REPORT_COMMUNITY_AND_IMPACT } from '../../../../../../shared/constants/tagManager';
import useInView from '../../../../../../shared/hooks/useInView/useInView';
import { IHealthReport } from '../../../../../../shared/interfaces/reports';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REPORTS } from '../../../../../../shared/translations/reports';

import './ChallengesSummaryData.scss';

export const ChallengesSummaryData = ({
  data,
  isLastReport,
  inDownloadMode
}: {
  data: IHealthReport;
  isLastReport?: boolean;
  inDownloadMode?: boolean;
}) => {
  const { t } = useTranslation();
  const { ref: containerRef, enterCount: cycle } = useInView<HTMLDivElement>({
    threshold: 0.3,
    enabled: !inDownloadMode
  });

  const info = useMemo(() => {
    const rules = [
      {
        condition: data.meditationUseIntention >= 20,
        titleKey: TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.meditation.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.meditation.description,
        percentage: data.meditationUseIntention
      },
      {
        condition: data.trainingUseIntention >= 20,
        titleKey: TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.training.title,
        descKey: TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.training.description,
        percentage: data.trainingUseIntention
      }
    ];

    const matched = rules.find((r) => r.condition);
    if (matched) {
      return {
        title: t(matched.titleKey),
        description: t(matched.descKey),
        percentage: matched.percentage
      };
    }

    const topChallenge = data.topChallenges[0] ?? {};
    const challengeType = t(
      topChallenge.typeName ?? TRANSLATIONS_REPORTS.healthReport.challengeData.challenges.defaultType
    ).toLowerCase();

    return {
      title: t(TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.challenge.title, {
        challenge_type: challengeType
      }),
      description: t(TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.challenge.description),
      percentage: topChallenge.percentage ?? 0
    };
  }, [data, t]);

  const goToChallenges = () => {
    TagManagerService.pushEvent(CLICK_ACTION, HEALTH_REPORT_COMMUNITY_AND_IMPACT, { type: 'challenge' });
    window.open(CLIENTS_PORTAL_URL + CREATE_CHALLENGE_ROUTE, '_blank');
  };

  return (
    <div className="challenges-summary-data-container" ref={containerRef}>
      <p className="csd-title">
        <span className="csd-percentage">
          {inDownloadMode ? (
            `${info.percentage}%`
          ) : (
            <CountUpNumber key={`csd-${cycle}`} value={info.percentage} duration={COUNT_UP_DURATION} suffix="%" />
          )}
        </span>
        {` ${info.title}`}
      </p>
      <p className={'health-report-description' + (isLastReport ? '' : ' csd-margin-bottom')}>{info.description}</p>
      {isLastReport && (
        <div className="csd-margin-bottom only-landing">
          <BdsButton variant="quaternary" size="s" action={goToChallenges}>
            {t(TRANSLATIONS_REPORTS.healthReport.challengesSummaryData.button)}
          </BdsButton>
        </div>
      )}
      <BdsDivider size="s" color="high" />
    </div>
  );
};

export default ChallengesSummaryData;
